import { memo, type ReactElement } from 'react';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { DeleteTestDialog, EditTestDialog } from '@/features/tests/components/dialogs/TestDialogs';
import { useTestsTableController } from '@/features/tests/components/useTestsTableController';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import type { TestsTableProps } from '@/features/tests/components/TestsTable.types';
import type { TestListItemDto } from '@flowqa/shared';

const TestsTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = true,
  disableColumnMenu = true,
  showToolbar = true,
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'test' }),
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  initialData,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  showSuiteLinks = true,
  includeSuiteInNameColumn = true,
  onStatusSelect,
  showDeltas = true,
  sparklineRangeDays = 7,
  disableLinks = false,
  search,
  metricsStartDay,
  metricsEndDay,
  enableMetricsBatch = true,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  tagOptionsOverride,
  showPaginationSkeletonWhenRowsVisible = false,
  preservePreviousData = false,
}: TestsTableProps): ReactElement => {
  const {
    columns,
    deleteLoading,
    deleteTarget,
    editEditor,
    editErrors,
    editForm,
    editTarget,
    gridConfig,
    handleEditClose,
    handleEditSave,
    handleRowsChange,
    isAuthenticated,
    onDeleteClose,
    onDeleteConfirm,
    queryFn: resolvedQueryFn,
    queryKey: resolvedQueryKey,
    selectedOrganisationId,
    selectedProjectId,
    togglableColumnFields,
  } = useTestsTableController({
    disableLinks,
    enableMetricsBatch,
    fixedFilters,
    includeSuiteInNameColumn,
    initialFilterModel,
    initialSortModel,
    metricsEndDay,
    metricsStartDay,
    onRowsChange,
    onStatusSelect,
    persistKey,
    queryFn,
    queryKey,
    showDeltas,
    showSuiteLinks,
    sparklineRangeDays,
    syncStateToUrl,
    tagOptionsOverride,
  });

  return (
    <>
      <ServerDataTable<TestListItemDto>
        initialData={initialData}
        queryPath="tests/query"
        {...gridConfig}
        queryFn={resolvedQueryFn}
        queryKey={resolvedQueryKey}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={isAuthenticated}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disablePageSizeSelect={disablePageSizeSelect}
        hideFooter={hideFooter}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        showToolbar={showToolbar}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        noRowsText={noRowsText}
        interactionMode="static"
        getRowHeight={() => 'auto'}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        showSkeletonOnFetch={showPaginationSkeletonWhenRowsVisible}
        showPaginationSkeletonWhenRowsVisible={showPaginationSkeletonWhenRowsVisible}
        preservePreviousData={preservePreviousData}
        showLastUpdated={showLastUpdated}
        autoHeight
        onRowsChange={handleRowsChange}
        onQueryMeta={onQueryMeta}
        onQueryInput={onQueryInput}
        onDataUpdatedAt={onDataUpdatedAt}
        gridProps={{
          getEstimatedRowHeight: () => TABLE_ROW_MEASURE_HEIGHT,
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={tableSx}
      />
      <DeleteTestDialog
        open={deleteTarget !== null}
        testName={deleteTarget?.name}
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <EditTestDialog
        open={editTarget !== null}
        saving={editEditor.isPending}
        values={editForm.values}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        errors={{ name: editErrors.fieldErrorProps('name') }}
        errorMessage={editForm.submitError}
        onClose={handleEditClose}
        onSave={handleEditSave}
        handlers={{
          onNameChange: (value) => {
            editForm.setField('name', value);
          },
        }}
      />
    </>
  );
};

export const TestsTable = memo(TestsTableComponent);
